import { useCallback, useEffect } from 'react';
import { Dimensions } from 'react-native';
import {
  Easing,
  FadeIn,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Owns every shared / derived value, animated style, and timing-related effect
 * for the combined permissions primer.
 */
export const usePermissionsPrimerAnimations = (params: Params) => {
  const { isPushPhase, isPushRequesting } = params;

  // ─── Shared Values ───────────────────────────────────────────────────

  const appIconOpacitySV = useSharedValue(1);
  const contentOpacitySV = useSharedValue(1);
  const hexagonRotateSV = useSharedValue(HEXAGON_PUSH_ROTATE_DEG);

  // ─── Derived Values ──────────────────────────────────────────────────

  const hexagonScaleSV = useDerivedValue<number>(() => {
    if (isPushPhase && isPushRequesting) {
      return withTiming(HEXAGON_LOCATION_INITIAL_SCALE, { duration: PHASE_TRANSITION_DURATION });
    }

    if (isPushPhase) {
      return HEXAGON_PUSH_SCALE;
    }

    return withDelay(
      HEXAGON_LOCATION_SHRINK_DELAY,
      withTiming(HEXAGON_LOCATION_FINAL_SCALE, { duration: HEXAGON_LOCATION_SHRINK_DURATION }),
    );
  }, [isPushPhase, isPushRequesting]);

  const hexagonTranslateXSV = useDerivedValue<number>(() => {
    if (isPushPhase && isPushRequesting) {
      return withTiming(LOCATION_TARGET_TRANSLATE_X, { duration: PHASE_TRANSITION_DURATION });
    }

    if (isPushPhase) {
      return 0;
    }

    return withTiming(LOCATION_TARGET_TRANSLATE_X, { duration: PHASE_TRANSITION_DURATION });
  }, [isPushPhase, isPushRequesting]);

  const hexagonTranslateYSV = useDerivedValue<number>(() => {
    if (isPushPhase && isPushRequesting) {
      return withTiming(LOCATION_TARGET_TRANSLATE_Y, { duration: PHASE_TRANSITION_DURATION });
    }

    if (isPushPhase) {
      return 0;
    }

    return withTiming(LOCATION_TARGET_TRANSLATE_Y, { duration: PHASE_TRANSITION_DURATION });
  }, [isPushPhase, isPushRequesting]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const triggerHexagonAnimation = useCallback(() => {
    if (isPushPhase) {
      hexagonRotateSV.value = withRepeat(
        withTiming(HEXAGON_PUSH_ROTATE_DEG + 360, { duration: HEXAGON_SPIN_DURATION, easing: Easing.linear }), // prettier-ignore
        -1,
        false,
      );

      return;
    }

    hexagonRotateSV.value = withTiming(0, { duration: PHASE_TRANSITION_DURATION });
  }, [isPushPhase, hexagonRotateSV]);

  const triggerAppIconAnimation = useCallback(() => {
    if (isPushPhase && isPushRequesting) {
      appIconOpacitySV.value = withTiming(0, { duration: PHASE_FADE_OUT_DURATION });

      return;
    }

    if (!isPushPhase) {
      appIconOpacitySV.value = 0;
    }
  }, [isPushPhase, isPushRequesting, appIconOpacitySV]);

  const triggerContentAnimation = useCallback(() => {
    if (isPushPhase && isPushRequesting) {
      contentOpacitySV.value = withTiming(0, { duration: PHASE_FADE_OUT_DURATION });

      return;
    }

    if (!isPushPhase) {
      contentOpacitySV.value = withDelay(
        LOCATION_CONTENT_REVEAL_DELAY,
        withTiming(1, { duration: PHASE_FADE_IN_DURATION }),
      );
    }
  }, [isPushPhase, isPushRequesting, contentOpacitySV]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const hexagonAnchorAnimatedStyles = useAnimatedStyle(() => ({
    transform: [
      { translateX: HEXAGON_PUSH_TRANSLATE_X + hexagonTranslateXSV.value },
      { translateY: HEXAGON_PUSH_TRANSLATE_Y + hexagonTranslateYSV.value },
    ],
  }));

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(triggerAppIconAnimation, [triggerAppIconAnimation]);
  useEffect(triggerContentAnimation, [triggerContentAnimation]);
  useEffect(triggerHexagonAnimation, [triggerHexagonAnimation]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    hexagonScaleSV,
    hexagonRotateSV,
    appIconOpacitySV,
    contentOpacitySV,
    hexagonAnchorAnimatedStyles,
    hexagonEntering: HEXAGON_ENTERING,
    hexagonLogoEntering: HEXAGON_LOGO_ENTERING,
    appIconEntering: APP_ICON_ENTERING,
    contentEnteringDelay: CONTENT_ENTERING_DELAY,
    locationStrokes: LOCATION_STROKES,
  };
};

// ─── Device ──────────────────────────────────────────────────────────────────

const { width: DEVICE_WIDTH, height: DEVICE_HEIGHT } = Dimensions.get('window');

const WIDE_DEVICE_BREAKPOINT = 440;
const IS_WIDER_DEVICE = DEVICE_WIDTH >= WIDE_DEVICE_BREAKPOINT;

// ─── Hexagon ─────────────────────────────────────────────────────────────────

const HEXAGON_INTRINSIC_SIZE = 1000;
const HEXAGON_SPIN_DURATION = 100_000;
const HEXAGON_ENTERING_DELAY = 400;
const HEXAGON_FADE_IN_DURATION = 750;

const HEXAGON_PUSH_SCALE = 0.63;
const HEXAGON_PUSH_ROTATE_DEG = 106;
const HEXAGON_PUSH_TRANSLATE_X = IS_WIDER_DEVICE ? 400 : 450;
const HEXAGON_PUSH_TRANSLATE_Y = -320;

const HEXAGON_LOCATION_INITIAL_SCALE = 1.1;
const HEXAGON_LOCATION_FINAL_SCALE = 0.06;
const HEXAGON_LOCATION_CENTER_X_RATIO = 0.5;
const HEXAGON_LOCATION_CENTER_Y_RATIO = 0.4;
const HEXAGON_LOCATION_SHRINK_DELAY = 400;
const HEXAGON_LOCATION_SHRINK_DURATION = 750;

const HEXAGON_PUSH_CENTER_X = DEVICE_WIDTH - HEXAGON_INTRINSIC_SIZE / 2 + HEXAGON_PUSH_TRANSLATE_X;
const HEXAGON_PUSH_CENTER_Y = HEXAGON_INTRINSIC_SIZE / 2 + HEXAGON_PUSH_TRANSLATE_Y;

const LOCATION_TARGET_TRANSLATE_X = HEXAGON_LOCATION_CENTER_X_RATIO * DEVICE_WIDTH - HEXAGON_PUSH_CENTER_X; // prettier-ignore
const LOCATION_TARGET_TRANSLATE_Y = HEXAGON_LOCATION_CENTER_Y_RATIO * DEVICE_HEIGHT - HEXAGON_PUSH_CENTER_Y; // prettier-ignore

// ─── Phase ───────────────────────────────────────────────────────────────────

const PHASE_TRANSITION_DURATION = 1200;
const PHASE_FADE_OUT_DURATION = 250;
const PHASE_FADE_IN_DURATION = 400;

// ─── App icon ────────────────────────────────────────────────────────────────

const APP_ICON_FADE_IN_DURATION = 700;
const APP_ICON_ENTERING_DELAY = 400;

// ─── Logo ────────────────────────────────────────────────────────────────────

const LOCATION_REVEAL_BUFFER = 150;
const LOGO_FADE_IN_DELAY = HEXAGON_LOCATION_SHRINK_DELAY + HEXAGON_LOCATION_SHRINK_DURATION + LOCATION_REVEAL_BUFFER; // prettier-ignore
const LOGO_FADE_IN_DURATION = 400;

// ─── Content ─────────────────────────────────────────────────────────────────

const CONTENT_ENTERING_DELAY = 400;

const LOCATION_CONTENT_AFTER_LOGO_BUFFER = 800;
const LOCATION_CONTENT_REVEAL_DELAY = LOGO_FADE_IN_DELAY + LOGO_FADE_IN_DURATION + LOCATION_CONTENT_AFTER_LOGO_BUFFER; // prettier-ignore

// ─── Location strokes ────────────────────────────────────────────────────────

const LOCATION_STROKES_COUNT = 5;
const LOCATION_ACTIVE_STROKES_COUNT = 3;
const LOCATION_STROKE_MIN_SCALE = 0.25;
const LOCATION_STROKE_MAX_SCALE = 1.3;
const LOCATION_STROKE_START_DELAY = LOGO_FADE_IN_DELAY;
const LOCATION_STROKE_DELAY_STEP = 120;

const LOCATION_STROKE_SCALE_RATIO = (LOCATION_STROKE_MAX_SCALE / LOCATION_STROKE_MIN_SCALE) ** (1 / (LOCATION_STROKES_COUNT - 1)); // prettier-ignore

const LOCATION_STROKES = Array.from({ length: LOCATION_STROKES_COUNT }, (_, index) => ({
  scale: LOCATION_STROKE_MIN_SCALE * LOCATION_STROKE_SCALE_RATIO ** index,
  delay: LOCATION_STROKE_START_DELAY + index * LOCATION_STROKE_DELAY_STEP,
  hideActiveOverlay: index < LOCATION_STROKES_COUNT - LOCATION_ACTIVE_STROKES_COUNT,
}));

// ─── Entering animations ─────────────────────────────────────────────────────

const HEXAGON_ENTERING = FadeIn.duration(HEXAGON_FADE_IN_DURATION).delay(HEXAGON_ENTERING_DELAY);
const HEXAGON_LOGO_ENTERING = FadeIn.duration(LOGO_FADE_IN_DURATION).delay(LOGO_FADE_IN_DELAY);
const APP_ICON_ENTERING = FadeIn.duration(APP_ICON_FADE_IN_DURATION).delay(APP_ICON_ENTERING_DELAY);

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  isPushPhase: boolean;
  isPushRequesting: boolean;
};
